import React, { useState } from "react";
import { Alert, Box, Button, Divider, Skeleton, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ConfirmDialog from "components/ConfirmDialog";
import SideDrawer, { SummaryRow, todayISODate } from "components/SideDrawer";
import StatusBadge from "components/StatusBadge";
import { useToast } from "components/Toast";
import {
  HOTEL_STATUS_LABELS,
  HOTEL_STATUS_TONES,
  HotelBooking,
  HotelTransaction,
  cancelHotelBooking,
  checkBookingStatus,
  getHotelBooking,
  nightsBetween,
  totalGuests,
} from "services/hotel";
import { ActivityItem, formatNaira, getApiError, getFinanceActivity } from "services/finance";
import { formatDisplayDate, formatUsd, toLocalDate } from "utils/helpers";

const CAN_CHECK_STATUS = ["processing", "failed"];
const CAN_CANCEL = ["confirmed"];

const Heading = ({ children }: { children: React.ReactNode }) => (
  <Typography fontSize={14} fontWeight={600} color="#101828" mt="16px" mb="4px">
    {children}
  </Typography>
);

const ETG_STATUS_LABELS: Record<string, string> = {
  ok: "Confirmed by ETG",
  processing: "Processing at ETG",
  error: "Failed at ETG",
};

const etgStatusLabel = (status?: string) =>
  status ? ETG_STATUS_LABELS[status] || status.replace(/_/g, " ") : "—";

const LedgerRow = ({ entry }: { entry: ActivityItem }) => {
  const expense = entry.type === "Expense";
  return (
    <Box display="flex" justifyContent="space-between" gap="12px" py="8px" borderBottom="1px solid #F2F4F7">
      <Box minWidth={0}>
        <Typography fontSize={14} color="#101828" fontWeight={500}>
          {entry.categoryLabel || entry.category?.replace(/_/g, " ") || "—"}
        </Typography>
        <Typography fontSize={12} color="#667085">
          {entry.reference} · {formatDisplayDate(entry.date)}
          {entry.affectsPnL === false ? " · not in P&L" : ""}
        </Typography>
      </Box>
      <Typography
        fontSize={14}
        fontWeight={600}
        color={expense ? "#B42318" : "#027A48"}
        whiteSpace="nowrap"
      >
        {expense ? "−" : "+"}
        {formatNaira(Math.abs(entry.amount || 0))}
      </Typography>
    </Box>
  );
};

interface HotelBookingPanelProps {
  booking: HotelBooking;
  onClose: () => void;
}

const HotelBookingPanel = ({ booking: listRow, onClose }: HotelBookingPanelProps) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const id = listRow.partner_order_id;

  // The list row is shown straight away; the public lookup adds ETG and guest details.
  const { data: detail } = useQuery({
    queryKey: ["hotelBooking", id],
    queryFn: () => getHotelBooking(id),
  });
  const booking: HotelBooking = { ...listRow, ...(detail || {}) };

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["hotelBooking", id] });
    queryClient.invalidateQueries({ queryKey: ["hotelBookings"] });
  };

  const checkStatus = useMutation({
    mutationFn: () => checkBookingStatus(id),
    onSuccess: (res) => {
      showToast(res?.message || "Status checked with ETG", "info");
      refresh();
    },
    onError: (error) => showToast(getApiError(error, "Failed to check ETG status"), "error"),
  });

  const cancel = useMutation({
    mutationFn: () => cancelHotelBooking(id),
    onSuccess: (res) => {
      showToast(res?.message || "Booking cancelled", "success");
      setConfirmCancel(false);
      refresh();
    },
    onError: (error) => {
      showToast(getApiError(error, "Failed to cancel booking"), "error");
      setConfirmCancel(false);
    },
  });

  const nights = nightsBetween(booking.checkin, booking.checkout);
  const guestNames = (booking.guest_details?.rooms || [])
    .flatMap((r) => r.guests || [])
    .map((g) => `${g.first_name} ${g.last_name}${g.is_child ? " (child)" : ""}`);
  // Only the admin list populates transactions; the public lookup returns ids.
  const transaction = listRow.transaction_id?.find(
    (t): t is HotelTransaction => typeof t === "object" && t !== null,
  );
  const etg = booking.etg_response;

  // Hotel bookings don't carry their ledger, so look the entries up in Finance
  // activity by order ID, from the booking date to today.
  const { data: activity, isLoading: ledgerLoading } = useQuery({
    queryKey: ["hotelBookingLedger", id],
    queryFn: () =>
      getFinanceActivity({
        search: id,
        start: listRow.createdAt ? toLocalDate(new Date(listRow.createdAt)) : undefined,
        end: todayISODate(),
        limit: 50,
      }),
  });
  const ledger = (activity?.data?.items || []).filter(
    (e) => e.bookingRef === id || (!e.bookingRef && e.description?.includes(id)),
  );
  const ledgerMargin = ledger
    .filter((e) => e.affectsPnL !== false)
    .reduce((sum, e) => sum + (e.type === "Expense" ? -1 : 1) * Math.abs(e.amount || 0), 0);

  return (
    <SideDrawer open onClose={onClose} title="Booking details" width={480}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap="8px">
        <Typography fontSize={13} color="#475467" sx={{ wordBreak: "break-all" }}>
          {id}
        </Typography>
        <StatusBadge status={booking.status} tones={HOTEL_STATUS_TONES} labels={HOTEL_STATUS_LABELS} />
      </Box>

      {etg?.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          ETG error: {etg.error}
        </Alert>
      )}

      <Heading>Stay</Heading>
      <SummaryRow label="Hotel" value={booking.hotel_name || "—"} />
      <SummaryRow
        label="Dates"
        value={`${formatDisplayDate(booking.checkin, false)} → ${formatDisplayDate(booking.checkout, false)}`}
      />
      <SummaryRow label="Nights" value={nights} />
      <SummaryRow label="Rooms" value={booking.rooms} />
      <SummaryRow label="Guests" value={totalGuests(booking.guests)} />
      {guestNames.length > 0 && <SummaryRow label="Guest names" value={guestNames.join(", ")} />}

      <Heading>Contact</Heading>
      <SummaryRow label="Name" value={booking.contact_info?.name || "—"} />
      <SummaryRow label="Email" value={booking.contact_info?.email || "—"} />
      <SummaryRow label="Phone" value={booking.contact_info?.phone || "—"} />

      <Heading>Price</Heading>
      <SummaryRow label="Total" value={formatNaira(booking.total_price)} bold />
      <SummaryRow label="Paid to ETG (USD)" value={formatUsd(booking.total_price_usd)} />
      <SummaryRow label="Rate at booking" value={`${formatNaira(booking.usd_ngn_rate)} / $1`} />
      {booking.payment_type && <SummaryRow label="Payment type" value={booking.payment_type} />}

      {transaction && (
        <>
          <Heading>Payment</Heading>
          <SummaryRow label="Status" value={transaction.status} />
          <SummaryRow label="Amount" value={formatNaira(transaction.amount)} />
          <SummaryRow label="Bank" value={transaction.active_bank || "—"} />
          <SummaryRow label="Reference" value={transaction.paymentReference || "—"} />
        </>
      )}

      <Heading>Ledger</Heading>
      {ledgerLoading ? (
        <Skeleton height={48} />
      ) : ledger.length ? (
        <>
          {ledger.map((e) => (
            <LedgerRow key={e.id || e.reference} entry={e} />
          ))}
          <SummaryRow
            label="Margin on this booking"
            value={
              <Box component="span" color={ledgerMargin < 0 ? "#B42318" : "#101828"}>
                {formatNaira(ledgerMargin)}
              </Box>
            }
            bold
          />
        </>
      ) : (
        <Typography fontSize={13} color="#667085" py="8px">
          No ledger entries for this booking yet.
        </Typography>
      )}

      <Heading>Supplier (ETG)</Heading>
      <SummaryRow label="ETG status" value={etgStatusLabel(etg?.status)} />
      {etg?.data?.percent !== undefined && etg?.data?.percent !== null && (
        <SummaryRow label="Paid to supplier upfront" value={`${etg.data.percent}%`} />
      )}
      {etg?.data?.prepayment ? (
        <SummaryRow label="Prepayment" value={String(etg.data.prepayment)} />
      ) : null}
      <SummaryRow label="Created" value={formatDisplayDate(booking.createdAt)} />
      {etg && (
        <>
          <Button size="small" onClick={() => setShowRaw(!showRaw)} sx={{ textTransform: "none", px: 0 }}>
            {showRaw ? "Hide raw response" : "Show raw response"}
          </Button>
          {showRaw && (
            <Box
              component="pre"
              bgcolor="#F9FAFB"
              p="12px"
              borderRadius="8px"
              fontSize={11}
              sx={{ overflowX: "auto", maxHeight: 260 }}
            >
              {JSON.stringify(etg, null, 2)}
            </Box>
          )}
        </>
      )}

      {(CAN_CHECK_STATUS.includes(booking.status) || CAN_CANCEL.includes(booking.status)) && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" gap="8px" flexWrap="wrap">
            {CAN_CHECK_STATUS.includes(booking.status) && (
              <Button
                variant="contained"
                disabled={checkStatus.isPending}
                onClick={() => checkStatus.mutate()}
                sx={{ textTransform: "none" }}
              >
                {checkStatus.isPending ? "Checking..." : "Check ETG status"}
              </Button>
            )}
            {CAN_CANCEL.includes(booking.status) && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => setConfirmCancel(true)}
                sx={{ textTransform: "none" }}
              >
                Cancel booking
              </Button>
            )}
          </Box>
        </>
      )}

      <ConfirmDialog
        open={confirmCancel}
        title="Cancel this booking?"
        message="The hotel is told straight away. Whether the customer gets a refund depends on the room's cancellation terms."
        confirmLabel="Cancel booking"
        destructive
        loading={cancel.isPending}
        onConfirm={() => cancel.mutate()}
        onClose={() => setConfirmCancel(false)}
      />
    </SideDrawer>
  );
};

export default HotelBookingPanel;
